// src/components/BCPCreateSheet.js
// 大阪府「超簡易版BCP『これだけは！』シート（自然災害対策版）」に準拠
import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';

// ---- モデルに存在するフィールド名一覧（これ以外は extraData に格納）----
const MODEL_FIELDS = new Set([
  'title','dateYear','dateMonth','dateDay',
  'companyName','location','contactPerson','method','purpose','remarksBasic',
  'contractor',
  'hazardSummary','earthquake','tsunami','flood','landslide','fire',
  'seismicScale',
  'communicationMethods','communicationOther',
  'responsible1','deputy1','responsible2','deputy2','responsible3','deputy3',
  'responsible4','deputy4','responsible5','deputy5',
  'member','inputPoint','inputLocation','inputLocationName',
  'workRaw','noWorkRaw',
  'waterStorage','foodStorage','toiletStorage','otherStorage',
  'insurance',
  'trainingMonth',
  'workRes','workFst','workNext','workImp',
  'customerRes','customerFst','customerNext','customerImp',
  'companyRes','companyFst','companyNext','companyImp',
  'importantRes','importantFst','importantNext','importantImp',
  'purposeRes','purposeFst','purposeNext','purposeImp',
  'nextReviewYear','nextReviewMonth',
  'extraData',
]);

const INITIAL = {
  // 1. 基本情報
  companyName: '', location: '', purpose: '', method: '', contractor: '',
  // 2. ハザードマップ - リスク記入欄（○/空）
  earthquake: '', tsunami: '', flood: '', riskInternalFlood: '', landslide: '', riskLiquefaction: '',
  // 2. BCP発動条件
  seismicScale: '',
  warningLevel4: false, warningLevel3: false,
  otherCondition1: false, otherConditionText: '',
  // 3. 組織体制
  communicationMethods: [], communicationOther: '',
  responsible1: '', deputy1: '',
  responsible2: '', deputy2: '',
  responsible3: '', deputy3: '',
  responsible4: '', deputy4: '',
  responsible5: '', deputy5: '',
  member: '', inputPoint: '', inputLocation: '', inputLocationName: '',
  // 4. 出社・帰宅体制
  goWork: false, goHome: false, stationRule: false, outsideContact: false, workRaw: '',
  stayHome: false, gatherMembers: false, noWorkRaw: '',
  // 5-1. 備蓄物資 
  countWater: '', waterStorage: '',
  countFood:  '', foodStorage: '',
  countToilet:'', toiletStorage: '',
  otherSupplyName: '', otherSupplyAmt: '', otherStorage: '',
  // 5-2. 訓練
  trainingMonth: '',
  // 5-3. 保険
  insurance: [], 
  // 6. 復旧
  workRes: '', workFst: '', workNext: '', workImp: '',
  customerRes: '', customerFst: '', customerNext: '', customerImp: '',
  companyRes: '', companyFst: '', companyNext: '', companyImp: '',
  importantRes: '', importantFst: '', importantNext: '', importantImp: '',
  purposeRes: '', purposeFst: '', purposeNext: '', purposeImp: '',
  // 宣言
  declareTraining: false, declareExtraStock: false,
  // 次回見直し
  nextReviewYear: '', nextReviewMonth: '',
};

// フォームデータを「モデルフィールド」と「extraData」に分割
function splitForApi(formData) {
  const main = {};
  const extra = {};
  Object.entries(formData).forEach(([k, v]) => {
    if (MODEL_FIELDS.has(k)) {
      main[k] = v;
    } else {
      extra[k] = v;
    }
  });
  main.extraData = extra;
  return main;
}

// APIレスポンスをフォームデータに戻す
function mergeFromApi(apiData) {
  const { extraData, id, user, updated_at, ...rest } = apiData;
  return { ...INITIAL, ...rest, ...(extraData || {}) };
}

// ===== コンポーネント =====
export default function BCPCreateSheet() {
  const [formData, setFormData] = useState(INITIAL);
  const [bcpId,    setBcpId]   = useState(null);
  const [saving,   setSaving]  = useState(false);
  const [saveMsg,  setSaveMsg] = useState(null);
  const [draftMsg, setDraftMsg]= useState(null);
  // タイムアウト競合防止: 前のタイマーをキャンセルしてから新しいものをセット
  const draftTimerRef = useRef(null);

  const showDraftMsg = (text, ms = 3500) => {
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    setDraftMsg(text);
    draftTimerRef.current = setTimeout(() => setDraftMsg(null), ms);
  };

  // 起動時：API → localStorage の順でデータ取得
  useEffect(() => {
    let cancelled = false;
    api.get('/bcp-detail/')
      .then(res => {
        if (cancelled) return;
        setBcpId(res.data.id ?? null);
        setFormData(mergeFromApi(res.data));
      })
      .catch(err => {
        if (cancelled) return;
        // 404 は「レコード未作成」= 正常。それ以外のみログ出力
        if (err?.response?.status !== 404) {
          console.error('BCP取得エラー:', err);
        }
        // localStorage ドラフトがあれば復元
        try {
          const draft = localStorage.getItem('bcp_draft');
          if (draft) {
            setFormData(prev => ({ ...prev, ...JSON.parse(draft) }));
            showDraftMsg('一時保存データを復元しました。', 4000);
          }
        } catch {}
      });
    // React StrictMode の二重呼び出しによる競合を防ぐクリーンアップ
    return () => { cancelled = true; };
  }, []);

  // テキスト/数値入力
  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // boolean チェックボックス
  const handleBool = e => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  // 配列チェックボックス（communicationMethods / insurance）
  const handleArr = (field, value, checked) => {
    setFormData(prev => {
      const arr = Array.isArray(prev[field]) ? prev[field] : [];
      return { ...prev, [field]: checked ? [...arr, value] : arr.filter(v => v !== value) };
    });
  };
  
  // 一時保存（localStorage）
  const handleDraft = () => {
    try {
      localStorage.setItem('bcp_draft', JSON.stringify(formData));
      showDraftMsg('一時保存しました。');
    } catch {
      showDraftMsg('一時保存に失敗しました。');
    }
  };

  // 本保存（API）
  const handleSubmit = async e => {
    e.preventDefault();
    if (!window.confirm('BCPシートを保存してもよろしいですか？')) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const payload = splitForApi(formData);
      let res;
      if (bcpId) {
        res = await api.put(`/bcp-detail/${bcpId}/`, payload);
      } else {
        res = await api.post('/bcp-detail/', payload);
        setBcpId(res.data.id ?? null);
      }
      localStorage.removeItem('bcp_draft');
      setSaveMsg({ type: 'success', text: 'BCPシートを保存しました。' });
    } catch (err) {
      console.error('BCP保存エラー:', err);
      setSaveMsg({ type: 'error', text: `保存に失敗しました: ${JSON.stringify(err?.response?.data ?? err.message)}` });
    } finally {
      setSaving(false);
    }
  };

  // ===== レンダリングヘルパー =====
  const inp = (name, placeholder = '', width = '100%') => (
    <input type="text" name={name} value={formData[name] ?? ''} onChange={handleChange}
      placeholder={placeholder}
      style={{ width, padding: '5px', border: '1px solid #ccc', borderRadius: 3, boxSizing: 'border-box', fontFamily: 'inherit' }} />
  );
  const ta = (name, rows = 2) => (
    <textarea name={name} value={formData[name] ?? ''} onChange={handleChange} rows={rows}
      style={{ width: '100%', padding: '5px', border: '1px solid #ccc', borderRadius: 3, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
  );
  const boolChk = (name, label) => (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 5, cursor: 'pointer', marginBottom: 3, lineHeight: 1.4 }}>
      <input type="checkbox" name={name} checked={Boolean(formData[name])} onChange={handleBool} style={{ marginTop: 2, flexShrink: 0 }} />
      <span>{label}</span>
    </label>
  );
  const arrChk = (field, value, label) => (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', marginRight: 14 }}>
      <input type="checkbox" checked={(formData[field] || []).includes(value)}
        onChange={e => handleArr(field, value, e.target.checked)} />
      {label}
    </label>
  );

  // セルスタイル
  const LBL  = { background: '#e6f7ff', fontWeight: 700, border: '1px solid #bbb', padding: '8px 10px', verticalAlign: 'top', whiteSpace: 'nowrap', fontSize: 13 };
  const CELL = { border: '1px solid #bbb', padding: '7px', verticalAlign: 'top', fontSize: 13 };
  const TH   = { background: '#f0f0f0', fontWeight: 700, border: '1px solid #bbb', padding: '6px', textAlign: 'center', fontSize: 13 };
  const SEC  = { border: '1px solid #bbb', marginBottom: 20, borderRadius: 4, overflow: 'hidden' };
  const SECH = { background: '#d9edf7', fontWeight: 700, fontSize: 15, padding: '8px 12px', borderBottom: '1px solid #bbb' };
  const SUBH = { background: '#fffbe6', fontWeight: 700, fontSize: 13, padding: '6px 12px', borderBottom: '1px solid #ddd', borderTop: '1px solid #ddd' };

  const today = new Date().toLocaleDateString('ja-JP');

  return (
    <div style={{ padding: '16px 20px', fontFamily: 'Meiryo, "Noto Sans JP", sans-serif', maxWidth: 1100, margin: '0 auto' }}>

      <div style={{ textAlign: 'right', fontSize: 12, color: '#666', marginBottom: 8 }}>
        作成日・最新更新日: {today}
      </div>

      {/* メッセージ */}
      {draftMsg && (
        <div style={{ padding: '7px 14px', marginBottom: 10, borderRadius: 4, background: '#fff3cd', color: '#856404', border: '1px solid #ffc107', fontSize: 13 }}>
          {draftMsg}
        </div>
      )}
      {saveMsg && (
        <div style={{
          padding: '8px 14px', marginBottom: 12, borderRadius: 4, fontSize: 13,
          background: saveMsg.type === 'success' ? '#d4edda' : '#f8d7da',
          color:      saveMsg.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${saveMsg.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
        }}>{saveMsg.text}</div>
      )}

      <form onSubmit={handleSubmit}>

        {/* ===== 1. 基本情報 ===== */}
        <div style={SEC}>
          <div style={SECH}>1. 基本情報</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ ...LBL, width: '14%' }}>企業名・屋号・<br/>工場名など</td>
                <td style={CELL}>{inp('companyName')}</td>
                <td style={{ ...LBL, width: '8%' }}>所在地</td>
                <td style={CELL}>{inp('location')}</td>
                <td style={{ ...LBL, width: '12%' }}>事業継続目標</td>
                <td style={CELL}>{inp('purpose')}</td>
              </tr>
              <tr>
                <td style={LBL}>事業継続方針</td>
                <td style={CELL} colSpan={3}>{ta('method', 2)}</td>
                <td style={LBL}>
                  主な委託先（※）<br/>
                  <span style={{ fontWeight: 'normal', fontSize: 11 }}>仕入先・協力会社・<br/>運送会社・派遣会社等</span>
                </td>
                <td style={CELL}>{ta('contractor', 2)}</td>
              </tr>
            </tbody>
          </table>
          <div style={{ padding: '4px 10px', fontSize: 11, color: '#555', borderTop: '1px solid #eee' }}>
            ※「主な委託先」とは、相手方の事業が中断した場合に自社の事業も中断してしまう関係性にある相手方のことです。
          </div>
        </div>

        {/* ===== 2. ハザードマップ ===== */}
        <div style={SEC}>
          <div style={SECH}>2. ハザードマップから考えるBCPの発動条件</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ ...LBL, width: '20%' }} rowSpan={3}>
                  ハザードマップを確認し、<br/>当社所在地で発生しうる<br/>災害リスクに「○」を記入
                  </td>
                <td style={{ ...LBL, width: '20%' }}>地震</td>
                <td style={{ ...CELL, width: '10%' }}>{inp('earthquake', '○')}</td>
                <td style={{ ...LBL, width: '20%' }}>津波</td>
                <td style={{ ...CELL, width: '10%' }}>{inp('tsunami', '○')}</td>
                </tr>
                <tr>
                <td style={LBL}>洪水・高潮（外水氾濫）</td>
                <td style={CELL}>{inp('flood', '○')}</td>
                <td style={LBL}>内水氾濫</td>
                <td style={CELL}>{inp('riskInternalFlood', '○')}</td>
                </tr>
                <tr>
                <td style={LBL}>土砂災害（地滑り等）</td>
                <td style={CELL}>{inp('landslide', '○')}</td>
                <td style={LBL}>液状化</td>
                <td style={CELL}>{inp('riskLiquefaction', '○')}</td>
              </tr>
            </tbody>
          </table>

          <div style={{ background: '#f9f9f9', padding: '6px 10px', fontSize: 12, borderTop: '1px solid #ddd', textAlign: 'center' }}>➡ 上記リスクを踏まえ、以下のBCP発動条件を設定します</div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ ...LBL, width: '20%' }} rowSpan={3}>所在地における<br/>当社のBCP発動条件</td>
                <td style={{ ...LBL, width: '15%' }}>地震</td>
                <td style={CELL}>
                  震度 {inp('seismicScale', '例: 5弱', 80)} 以上を観測した場合
                </td>
                </tr>
                <tr>
                <td style={LBL}>水害・土砂災害</td>
                <td style={CELL}>
                  {boolChk('warningLevel4', '警戒レベル４「避難指示」（危険な場所から全員避難）')}
                  {boolChk('warningLevel3', '警戒レベル３「高齢者等避難」（危険な場所から高齢者等は避難）')}
                  </td>
                </tr>
                <tr>
                <td style={LBL}>その他（※）</td>
                <td style={CELL}>
                  {boolChk('otherCondition1', '主な委託先からの製品・サービスの供給の途絶')}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                    <span>その他（</span>{inp('otherConditionText', '自由記入', 200)}<span>）</span>
                  </label>
                  </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ===== 3. BCPの発動時の組織体制 ===== */}
        <div style={SEC}>
          <div style={SECH}>3. BCPの発動時の組織体制</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {/* 連絡方法 */}
              <tr>
                <td style={LBL}>従業者間の連絡方法</td>
                <td style={CELL} colSpan={4}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                    {arrChk('communicationMethods', 'メール', 'メール')}
                    {arrChk('communicationMethods', 'SNS（LINE等）', 'SNS（LINE等でグループ作成）')}
                    {arrChk('communicationMethods', '電話', '電話')}
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      その他（{inp('communicationOther', '', 120)}）
                    </label>
                  </div>
                  <p style={{ fontSize: 11, color: '#666', margin: '4px 0 0' }}>
                    ※ 従業者のご家族同士も安否確認の方法を決めておくことが大切です！
                  </p>
                </td>
              </tr>
              
              {/* BCP担当 ①〜⑤ */}
              {[
                ['①', 'responsible1', 'deputy1', '情報担当責任者',
                  '最新の災害及び被害に関する情報を収集するとともに、社内外への情報発信を行う。'],
                ['②', 'responsible2', 'deputy2', '供給担当責任者',
                  '災害発生時・事業中断時における取引先や消費者に対する供給責任に関する対応を行う。'],
                ['③', 'responsible3', 'deputy3', '予算担当責任者',
                  '災害発生時に必要とされる予算の管理を行う。（設備復旧費用・資金繰り等）'],
                ['④', 'responsible4', 'deputy4', '現場担当責任者',
                  '現場での事業継続目標をふまえた初動対応及び復旧対策について対応を行う。'],
                ['⑤', 'responsible5', 'deputy5', '特命担当責任者',
                  '①〜④の役割と責任の範囲外で別途対応が必要な場合に選任する。（例：法務等）'],
              ].map(([no, rk, dk, title, desc], i) => (
                <tr key={no}>
                  {i === 0 && (
                    <td style={{ ...LBL, fontSize: 12 }} rowSpan={5}>
                      BCP担当<br/>
                      <span style={{ fontWeight: 'normal', fontSize: 11 }}>
                        ◎社長が対応できない場合に<br/>社長の代理として司令塔を担う方<br/>
                        ◎複数選任する場合は継承順位も記載
                  </span>
                </td>
                  )}
                  <td style={{ ...LBL, width: '18%', fontSize: 12 }}>{no}&nbsp;{title}</td>
                  <td style={{ ...CELL, fontSize: 12, width: '30%' }}>{desc}</td>
                  <td style={{ ...CELL, width: '14%' }}>担当：{inp(rk)}</td>
                  <td style={{ ...CELL, width: '14%' }}>副担当：{inp(dk)}</td>
              </tr>
              ))}

              <tr>
                <td style={LBL}>就業時間外発災時<br/>参集メンバー</td>
                <td style={CELL} colSpan={4}>
                  {inp('member', '経営者層や管理職、会社に速やかに参集できるメンバーをご記入ください')}
                </td>
              </tr>
              <tr>
                <td style={LBL}>代替拠点</td>
                <td style={CELL} colSpan={4}>{inp('inputPoint')}</td>
              </tr>
              <tr>
                <td style={LBL}>避難場所</td>
                <td style={CELL} colSpan={4}>
                  {inp('inputLocation')}
                  <p style={{ fontSize: 11, color: '#666', margin: '4px 0 0' }}>
                    ※ 地震・津波・高潮・洪水・土砂災害・大規模火災などの場合、職場の安全が保てない場合の避難場所及び避難所の確認をしておいてください。
                  </p>
                </td>
              </tr>
              <tr>
                <td style={LBL}>避難所</td>
                <td style={CELL} colSpan={4}>{inp('inputLocationName')}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ===== 4. 発災時の出社・帰宅体制 ===== */}
        <div style={SEC}>
          <div style={SECH}>4. 発災時の出社・帰宅体制（休日含む）</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
                <tr>
                <th style={TH}></th>
                <th style={{ ...TH, background: '#fffbe6', width: '45%' }}>原則（適用するものに☑）</th>
                <th style={{ ...TH, background: '#fffbe6', width: '35%' }}>自社独自ルール</th>
                </tr>
            </thead>
            <tbody>
              <tr>
                <td style={LBL}>出社・帰宅時間帯<br/>に発災</td>
                <td style={CELL}>
                  {boolChk('goWork', '（出社時）原則、自宅待機又は自宅に戻る。職場に近い場合は職場へ。')}
                  {boolChk('goHome', '（帰宅時）原則、職場内待機又は職場に戻る。自宅に近い場合は自宅へ。')}
                  {boolChk('stationRule', '駅等にいる場合は、公共交通機関等の指示に従う。長時間行き場がない場合は、避難所等へ避難する。')}
                  {boolChk('outsideContact', '職場以外の場合は、必ず会社に連絡する。')}
                </td>
                <td style={CELL}>{ta('workRaw', 4)}</td>
              </tr>
              <tr>
                <td style={LBL}>就業時間外に発災<br/>（休日等）</td>
                <td style={CELL}>
                  {boolChk('stayHome', '自宅待機。')}
                  {boolChk('gatherMembers', '身の安全を優先したうえで、「就業時間外発災時参集メンバー」は出社。')}
                </td>
                <td style={CELL}>{ta('noWorkRaw', 4)}</td>
              </tr>
            </tbody>
          </table>
          <div style={{ padding: '6px 10px', fontSize: 11, color: '#555', borderTop: '1px solid #eee' }}>
            ※ 上記ルールは、発災後すぐに逃げないといけないような津波等の災害の場合は除きます。（人命の安全確保が最優先です。）
          </div>
        </div>

        {/* ===== 5. 減災の事前対策 ===== */}
        <div style={SEC}>
          <div style={SECH}>5. 減災の事前対策</div>

          {/* 5-1 */}
          <div style={SUBH}>5-1. 備蓄物資（従業者1人に対し最低3日分が目安）</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#fffbe6' }}>
                <th style={TH}>備蓄物資</th>
                <th style={TH}>備蓄完了<br/>チェック</th>
                <th style={TH}>1人あたりの必要量</th>
                <th style={TH}>× 3日 =</th>
                <th style={TH}>必要量</th>
                <th style={TH}>従業者数（人）</th>
                <th style={TH}>最低限必要な備蓄量</th>
                <th style={TH}>保管場所</th>
                </tr>
            </thead>
            <tbody>
              {[
                { label: '飲料水',    unit: '1日3リットル', total: '9リットル',  ck: 'countWater',  sk: 'waterStorage' },
                { label: '食料',      unit: '1日3食',      total: '9食',        ck: 'countFood',   sk: 'foodStorage' },
                { label: '簡易トイレ', unit: '1日5回分',   total: '15回分',     ck: 'countToilet', sk: 'toiletStorage' },
              ].map(r => (
                <tr key={r.label}>
                  <td style={{ ...LBL, textAlign: 'center' }}>{r.label}</td>
                  <td style={{ ...CELL, textAlign: 'center' }}><input type="checkbox" /></td>
                  <td style={{ ...CELL, textAlign: 'center' }}>{r.unit}</td>
                  <td style={{ ...CELL, textAlign: 'center' }}>× 3日 =</td>
                  <td style={{ ...CELL, textAlign: 'center' }}>{r.total}</td>
                  <td style={{ ...CELL, textAlign: 'center' }}>
                    <input type="text" name={r.ck} value={formData[r.ck] ?? ''} onChange={handleChange}
                      style={{ width: 60, textAlign: 'center', border: '1px solid #ccc', padding: '4px', borderRadius: 3 }} />
                    <span style={{ fontSize: 11 }}> 人</span>
                  </td>
                  <td style={{ ...CELL, textAlign: 'center' }}>{r.total}</td>
                  <td style={CELL}>{inp(r.sk)}</td>
              </tr>
              ))}
              <tr>
                <td style={{ ...LBL, textAlign: 'center' }}>その他</td>
                <td style={{ ...CELL, textAlign: 'center' }}><input type="checkbox" /></td>
                <td style={CELL}>{inp('otherSupplyName')}</td>
                <td style={{ ...CELL, textAlign: 'center' }}>× 3日 =</td>
                <td style={CELL}></td>
                <td style={{ ...CELL, textAlign: 'center' }}>
                  <input type="text" name="otherSupplyAmt" value={formData.otherSupplyAmt ?? ''} onChange={handleChange}
                    style={{ width: 60, textAlign: 'center', border: '1px solid #ccc', padding: '4px', borderRadius: 3 }} />
                </td>
                <td style={CELL}></td>
                <td style={CELL}>{inp('otherStorage')}</td>
              </tr>
            </tbody>
          </table>
          
          {/* 5-2 */}
          <div style={SUBH}>5-2. 訓練・演習の実施</div>
          <div style={{ padding: '8px 12px', background: '#f9f9f9', fontSize: 13 }}>
            従業者や拠点間の連絡手段を用いた訓練、出社・帰宅に関する訓練、発災時を想定したシナリオに基づく演習に取り組む。
            従業者には自社のBCPの取り組みを教育（入社時、定期的な研修等）。
            <div style={{ marginTop: 6 }}>
              毎年{' '}
              <input type="text" name="trainingMonth" value={formData.trainingMonth ?? ''} onChange={handleChange}
                style={{ width: 44, textAlign: 'center', border: '1px solid #ccc', padding: '4px', borderRadius: 3 }}
                placeholder="月" />
              {' '}月に実施
            </div>
                  </div>

          {/* 5-3 */}
          <div style={SUBH}>5-3. 保険共済への加入</div>
          <div style={{ padding: '10px 12px', display: 'flex', flexWrap: 'wrap', gap: 12, background: '#fdfdff', fontSize: 13 }}>
            {[['fire','火災保険'],['earth','地震特約'],['holiday','休業対応応援共済'],
              ['workday','業務災害保険'],['social','福祉共済'],['life','生命保険'],['others','その他']
            ].map(([id, label]) => (
              <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input type="checkbox" checked={(formData.insurance || []).includes(label)}
                  onChange={e => handleArr('insurance', label, e.target.checked)} />
                {label}
              </label>
              ))}
          </div>
        </div>

        {/* ===== 6. 復旧に向けて ===== */}
        <div style={SEC}>
          <div style={SECH}>6. BCPの発動時から復旧に向けて</div>
          <div style={{ padding: '4px 12px', fontSize: 12, color: '#555' }}>
            経営資源（人・物・金・情報）及び事業継続目標を意識した行動を行います。「担当」欄には上記「3.」の各担当責任者の番号をご記入ください。
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TH}></th>
                <th style={TH}>担当</th>
                <th style={TH}>まず最初に取り組むこと</th>
                <th style={TH}>次に取り組むこと</th>
                <th style={TH}>必要な事前対策</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['従業者の安全確認',             'workRes',     'workFst',     'workNext',     'workImp'],
                ['来訪者の安全確認',             'customerRes', 'customerFst', 'customerNext', 'customerImp'],
                ['自社の被害状況の把握',          'companyRes',  'companyFst',  'companyNext',  'companyImp'],
                ['主な委託先の被災状況の把握',     'importantRes','importantFst','importantNext','importantImp'],
                ['事業継続目標を踏まえた\n早期復旧への取り組み','purposeRes','purposeFst','purposeNext','purposeImp'],
              ].map(([label, r, f, n, i]) => (
                <tr key={label}>
                  <td style={{ ...LBL, whiteSpace: 'pre-line' }}>{label}</td>
                  <td style={CELL}>{inp(r)}</td>
                  <td style={CELL}>{inp(f)}</td>
                  <td style={CELL}>{inp(n)}</td>
                  <td style={CELL}>{inp(i)}</td>
              </tr>
              ))}
            </tbody>
          </table>

          {/* ◎ 宣言 */}
          <div style={{ padding: '10px 12px', background: '#f9f9f9', borderTop: '1px solid #ddd', fontSize: 13 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>◎ 宣言（共助の観点から、地域社会のため、以下についても宣言しましょう。）</div>
            {boolChk('declareTraining',
              '職場周辺の地域が行う災害訓練には積極的に参加します。また、災害発生時は、充分な身体防護対策をとり、2次災害が起きないよう最大限の配慮を行った上で、救助・消火活動等に協力します。')}
            <div style={{ marginTop: 4 }}>
              {boolChk('declareExtraStock', '帰宅困難者や地域に提供するため、1割増しの備蓄物資の確保に努めます。')}
            </div>
          </div>

          {/* 次回見直し */}
          <div style={{ padding: '8px 12px', borderTop: '1px solid #ddd', fontSize: 13 }}>
            当社の「これだけは！」シート（自然災害対策版）は、次回{' '}
            <input type="text" name="nextReviewYear" value={formData.nextReviewYear ?? ''} onChange={handleChange}
              style={{ width: 52, textAlign: 'center', border: '1px solid #ccc', padding: '4px', borderRadius: 3 }} placeholder="年" /> 年{' '}
            <input type="text" name="nextReviewMonth" value={formData.nextReviewMonth ?? ''} onChange={handleChange}
              style={{ width: 40, textAlign: 'center', border: '1px solid #ccc', padding: '4px', borderRadius: 3 }} placeholder="月" /> 月に見直します。
          </div>
        </div>

        {/* ===== ボタン ===== */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0 24px' }}>
          <button type="button" onClick={handleDraft}
            style={{ padding: '10px 24px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 4, fontSize: 14, cursor: 'pointer' }}>
            一時保存
          </button>
          <button type="submit" disabled={saving}
            style={{ padding: '10px 28px', background: saving ? '#aaa' : '#1e90ff', color: '#fff', border: 'none', borderRadius: 4, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? '保存中…' : '保存'}
        </button>
          {bcpId && <span style={{ fontSize: 12, color: '#888' }}>※ 既存データを更新します（ID: {bcpId}）</span>}
        </div>

      </form>
    </div>
  );
}
